import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import { toast } from 'sonner';
import { Mail, Lock } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminLogin = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [loading, setLoading] = useState(false);
  const [sessionExpired, setSessionExpired] = useState(false);

  useEffect(() => {
    if (searchParams.get('expired') === 'true') {
      setSessionExpired(true);
    }
  }, [searchParams]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post(`${API}/admin/login`, formData);
      
      localStorage.setItem('admin_token', response.data.access_token);
      
      toast.success('Welcome back, Anvita!');
      navigate('/admin');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid credentials');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen pt-20 px-6 py-16 flex items-center justify-center" data-testid="admin-login-page">
      <Card className="w-full max-w-md bg-white/80 backdrop-blur-sm rounded-3xl p-8 shadow-xl">
        <div className="text-center mb-8">
          <h1 className="text-4xl font-heading font-light text-foreground mb-2" data-testid="admin-login-heading">Admin Login</h1>
          <p className="text-muted-foreground">Access your dashboard</p>
          {sessionExpired && (
            <div className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded-lg">
              <p className="text-amber-700 text-sm font-medium">Your session has expired. Please sign in again.</p>
            </div>
          )}
        </div>

        <form onSubmit={handleSubmit} className="space-y-6" data-testid="admin-login-form">
          <div>
            <Label htmlFor="email" className="text-foreground font-medium">Email</Label>
            <div className="relative mt-2">
              <Mail className="absolute left-3 top-3 text-muted-foreground" size={20} />
              <Input
                id="email"
                name="email"
                type="email"
                required
                value={formData.email}
                onChange={handleChange}
                className="pl-10 bg-white/50 border-muted focus:border-primary rounded-lg"
                placeholder="admin@email.com"
                data-testid="admin-email-input"
              />
            </div>
          </div>

          <div>
            <Label htmlFor="password" className="text-foreground font-medium">Password</Label>
            <div className="relative mt-2">
              <Lock className="absolute left-3 top-3 text-muted-foreground" size={20} />
              <Input
                id="password"
                name="password"
                type="password"
                required
                value={formData.password}
                onChange={handleChange}
                className="pl-10 bg-white/50 border-muted focus:border-primary rounded-lg"
                placeholder="Enter your password"
                data-testid="admin-password-input"
              />
            </div>
          </div>

          <Button
            type="submit"
            disabled={loading}
            className="w-full bg-primary text-white hover:bg-primary/90 rounded-full py-6 text-lg"
            data-testid="admin-login-submit"
          >
            {loading ? 'Signing In...' : 'Sign In'}
          </Button>
        </form>
      </Card>
    </div>
  );
};

export default AdminLogin;